import { v4 as uuid } from "uuid";
import { Issue, IssueAttributes } from "@adsk/acc-issues-types";
import { useDispatch } from "@adsk/acc-issues-core/state/redux/hooks";
import { createIssueAction } from "../../exports/redux";
import { analyticsDataProperties } from "@adsk/acc-issues-core/state/redux/ducks/issuesCollection";
import { useCallback } from "react";
import { useIssueSnapshotFlow } from "../IssueDetailPanel/IssueDetailTabPanel/hooks/IssueSnapshot";
import { IssueSnapshotCreateTrigger } from "../utils/analytics/analyticsLib093IssuesEvents";
import { useDefaultOriginContextPayload } from "./useDefaultOriginContextPayload";
import {
  createCustomEvent,
  useFireCustomEvent,
} from "@adsk/acc-issues-core/providers/CustomEventContext";
import { CustomEventType } from "@adsk/acc-issues-types";

type createIssueFnType = ({
  issueAttributes,
  customCreateFn,
  analyticsData,
  onSuccess,
  onFailure,
  snapshotBlob,
}: {
  issueAttributes: IssueAttributes;
  customCreateFn?: (issueAttributes: IssueAttributes) => Promise<Issue>;
  analyticsData?: analyticsDataProperties;
  onSuccess?: (newIssue: Issue) => any;
  onFailure?: (error: Error) => void;
  snapshotBlob?: Promise<Blob | undefined>;
}) => Promise<Issue>;

/**
 * Returns a function that creates an issue and reports an issue creation analytic if successful. Custom overrides can be provided for the issue creation function, the on issue creation success and on issue creation failure functions.
 */
export const useCreateIssue = (): createIssueFnType => {
  //Making sure that the default for captureSnapshot is true
  const dispatch = useDispatch();

  const { uploadSnapshot, setSnapshotAsAThumbnail, getFlowId } =
    useIssueSnapshotFlow();
  const fireCustomEvent = useFireCustomEvent();

  const defaultOriginContextPayload = useDefaultOriginContextPayload();

  return useCallback(
    async ({
      issueAttributes,
      customCreateFn,
      onSuccess,
      onFailure,
      analyticsData = {},
      snapshotBlob,
    }) => {
      const issuePayload = { id: uuid(), ...issueAttributes };
      const flowId = getFlowId(issuePayload.id);
      let storageUrnPromise = undefined;

      if (snapshotBlob) {
        // Avoid waiting so that the upload can happen in parallel with the issue creation
        storageUrnPromise = uploadSnapshot({
          issue: issuePayload,
          snapshotBlob: await snapshotBlob,
          hasMarkups: false,
          flowId,
        });

        issuePayload.skipThumbnailGeneration = true;
      }

      const placements =
        issueAttributes.placements ?? issueAttributes.linkedDocuments;

      if (defaultOriginContextPayload && placements) {
        for (const placement of placements) {
          placement.originContext ??= defaultOriginContextPayload;
        }
      }

      const issue = await dispatch(
        createIssueAction({
          issueData: issuePayload,
          onSuccess,
          onFailure,
          analyticsData,
          customCreateFn,
        })
      );

      if (issue === null) {
        throw new Error("Failed to create issue");
      }

      if (storageUrnPromise) {
        const storageUrn = await storageUrnPromise;
        setSnapshotAsAThumbnail({
          issue,
          storageUrn,
          hasMarkups: false,
          flowId,
          trigger: IssueSnapshotCreateTrigger.CREATE_ISSUE,
        });
      }

      const customEventData = createCustomEvent({
        type: CustomEventType.IssueCreated,
        target: null,
        data: { issues: [issue] },
      });

      if (fireCustomEvent) {
        fireCustomEvent(customEventData);
      }

      return issue;
    },
    [
      defaultOriginContextPayload,
      dispatch,
      fireCustomEvent,
      getFlowId,
      setSnapshotAsAThumbnail,
      uploadSnapshot,
    ]
  );
};
